import React, { useState, useEffect } from 'react';
import { t } from '../i18n';

const CATEGORIES = [
  { value: 'bug', label: '🐛 Bug Report' },
  { value: 'payment', label: '💰 Payment Issue' },
  { value: 'account', label: '👤 Account Issue' },
  { value: 'content', label: '📝 Content Issue' },
  { value: 'feature', label: '💡 Feature Request' },
  { value: 'other', label: '📋 Other' },
];

const STATUS_COLOR = {
  open: '#0d9488', in_progress: '#3b82f6',
  resolved: '#10b981', closed: '#999'
};

function Support({ user }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', category: 'bug' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => { if (user) fetchTickets(); }, [user]);

  const fetchTickets = async () => {
    try {
      const res = await fetch('/api/support/', {
        headers: { 'Authorization': `Token ${user.token}` }
      });
      setTickets(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const submitTicket = async () => {
    if (!form.title || !form.body) { alert('Please fill all fields!'); return; }
    setSubmitting(true);
    try {
      const res = await fetch('/api/support/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      alert(data.message || 'Ticket submitted!');
      setShowForm(false);
      setForm({ title: '', body: '', category: 'bug' });
      fetchTickets();
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  if (!user) return (
    <div style={styles.container}>
      <h2 style={{ textAlign: 'center', color: '#6200ea' }}>{t('sp_login')}</h2>
    </div>
  );

  if (selectedTicket) return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => setSelectedTicket(null)}>{t('sp_back')}</button>
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <h2 style={{ color: '#6200ea', margin: 0, fontSize: '18px' }}>{selectedTicket.title}</h2>
          <span style={{ ...styles.badge, backgroundColor: STATUS_COLOR[selectedTicket.status] }}>
            {selectedTicket.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div style={styles.meta}>
          {CATEGORIES.find(c => c.value === selectedTicket.category)?.label} · {selectedTicket.created_at}
        </div>
        <div style={styles.ticketBody}>{selectedTicket.body}</div>
        {selectedTicket.admin_reply && (
          <div style={styles.adminReply}>
            <div style={{ fontWeight: 'bold', color: '#6200ea', marginBottom: '8px' }}>{t('sp_reply')}</div>
            <p style={{ margin: 0, color: '#333' }}>{selectedTicket.admin_reply}</p>
          </div>
        )}
        {!selectedTicket.admin_reply && (
          <div style={{ backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '10px', color: '#999', fontSize: '13px', marginTop: '12px' }}>
            ⏳ Awaiting admin response...
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('sp_title')}</h1>
        <button style={styles.newBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '+ New Ticket'}
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h3 style={{ color: '#6200ea', marginBottom: '16px' }}>{t('sp_new')}</h3>
          <select style={styles.input} value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input style={styles.input} placeholder={t('sp_t_ph')} value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          <textarea style={styles.textarea} placeholder={t('sp_d_ph')} rows={5} value={form.body} onChange={e => setForm({...form, body: e.target.value})} />
          <button style={styles.submitBtn} onClick={submitTicket} disabled={submitting}>
            {submitting ? 'Submitting...' : '🚀 Submit Ticket'}
          </button>
        </div>
      )}

      {loading ? <div style={styles.loading}>{t('sp_loading')}</div> :
        tickets.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ fontSize: '48px' }}>🎫</p>
            <p>{t('sp_none')}</p>
            <p style={{ fontSize: '13px', color: '#999' }}>{t('sp_none_help')}</p>
          </div>
        ) : (
          <div style={styles.list}>
            {tickets.map(t => (
              <div key={t.id} style={styles.ticketCard} onClick={() => setSelectedTicket(t)}>
                <div style={styles.ticketHeader}>
                  <span style={styles.ticketTitle}>{t.title}</span>
                  <span style={{ ...styles.badge, backgroundColor: STATUS_COLOR[t.status] }}>
                    {t.status.replace('_', ' ')}
                  </span>
                </div>
                <div style={styles.meta}>
                  {CATEGORIES.find(c => c.value === t.category)?.label} · {t.created_at}
                </div>
                <p style={styles.ticketPreview}>{t.body.substring(0, 100)}...</p>
                {t.admin_reply && (
                  <div style={{ fontSize: '12px', color: '#10b981', marginTop: '6px' }}>{t('sp_replied')}</div>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

const styles = {
  container: { maxWidth: '700px', margin: '20px auto', padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { color: '#6200ea', margin: 0 },
  newBtn: { backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  backBtn: { backgroundColor: '#f3e5f5', color: '#6200ea', border: 'none', padding: '8px 16px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', marginBottom: '16px' },
  card: { backgroundColor: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '16px' },
  input: { width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  submitBtn: { width: '100%', backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '14px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' },
  loading: { textAlign: 'center', padding: '40px', color: '#999' },
  empty: { textAlign: 'center', padding: '60px', color: '#666' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  ticketCard: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer' },
  ticketHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '8px' },
  ticketTitle: { fontWeight: 'bold', color: '#333', fontSize: '15px', flex: 1 },
  badge: { color: 'white', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 },
  meta: { color: '#999', fontSize: '12px', marginBottom: '8px' },
  ticketPreview: { color: '#666', fontSize: '13px', margin: 0 },
  ticketBody: { color: '#333', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: '12px' },
  adminReply: { backgroundColor: '#f3e5f5', padding: '14px', borderRadius: '10px', border: '2px solid #6200ea', marginTop: '12px' },
};

export default Support;
